import { Form, MessageBox, Typography } from '@s4l/ds'
import { IconEmail } from '@s4l/icons/IconEmail'
import { useS4LContext } from '@s4l/react-context'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import { Devices, useBrowser } from '@s4l/utils/hooks/useBrowser'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'
import { isRTL } from '@s4l/utils/library/rtl'
import React, { type CSSProperties } from 'react'
import FormErrorDisplay from './FormErrorDisplay'
import styles from './styles.module.css'

export interface SubscriptionBoxProps {
  source: string
  bgColor?: string
  variant?: 'section' | 'box' | 'minimal'
  title?: string
  description?: string
  buttonText?: string
  buttonBgColor?: string
  buttonTextColor?: string
}

function SubscriptionBox({
  source,
  bgColor = 'var(--color-base-white)',
  variant = 'section',
  title,
  description,
  buttonText,
  buttonBgColor,
  buttonTextColor,
}: Readonly<SubscriptionBoxProps>) {
  const { i18n } = useS4LContext()
  const { device } = useBrowser()

  const isMinimal = variant === 'minimal'
  const isSection = variant === 'section'
  const rtl = isRTL()

  const imagesBasePath = `${process.env.NEXT_PUBLIC_CLOUDFRONT_ENDPOINT}/images/subscription_box`

  const {
    data: subscriptionStatus,
    loading: isLoading,
    error: useCaseError,
    execute: subscribe,
  } = useUseCase('SubscribeToNewsletterMarketingUseCase', { lazy: true })

  const handleSubmit = (formData: { email: string }) => {
    subscribe({
      email: formData.email,
      source,
    })
  }

  const displaySuccessMessage =
    subscriptionStatus?.value === 'subscribed' || subscriptionStatus?.value === 'pending'

  const formContent = (
    <div
      className={styles.base}
      data-variant={variant}
      style={
        {
          '--bg-color': bgColor,
          '--button-bg-color': buttonBgColor,
          '--button-text-color': buttonTextColor,
        } as CSSProperties
      }
      data-testid="subscription-box-new"
    >
      {!isMinimal && (
        <>
          <Typography
            as="h5"
            variant="title"
            size={device === Devices.Mobile ? 'xs' : 's'}
            color="var(--color--neutral--800)"
            bold
          >
            {title ||
              i18n.formatMessage({
                id: 'subscription.subscribe-to-get-unique-discounts',
              })}
          </Typography>
          <Typography as="p" variant="body" size="l" color="var(--color--neutral--800)">
            {description || i18n.formatMessage({ id: 'subscription.on-our-newsletter' })}
          </Typography>
        </>
      )}
      {displaySuccessMessage ? (
        <MessageBox kind="success">
          <MessageBox.Title>
            {i18n.formatMessage({ id: 'subscription.thank-you-for-being-part-of-our-community' })}
          </MessageBox.Title>
        </MessageBox>
      ) : (
        <Form onSubmit={handleSubmit}>
          <div className={styles.formLayout}>
            <div className={styles.inputContainer}>
              <Form.Input
                name="email"
                placeholder={i18n.formatMessage({ id: 'global.email' })}
                startIcon={<IconEmail size={24} />}
                validations={[
                  Form.Validations.required({
                    message: i18n.formatMessage({ id: 'validation.fill-field' }),
                  }),
                  Form.Validations.email({
                    message: i18n.formatMessage({ id: 'input.valid-email' }),
                  }),
                ]}
                disabled={isLoading}
              />
              <FormErrorDisplay useCaseError={useCaseError} />
            </div>
            <div
              className={styles.submitButton}
              {...(buttonBgColor && { 'data-custom-bg': 'true' })}
              {...(buttonTextColor && { 'data-custom-color': 'true' })}
            >
              <Form.SubmitButton isLoading={isLoading}>
                {buttonText || i18n.formatMessage({ id: 'subscription.subscribe-cta' })}
              </Form.SubmitButton>
            </div>
          </div>
        </Form>
      )}
    </div>
  )

  if (isSection) {
    return (
      <div className={styles.sectionWrapper}>
        <div
          className={styles.imageContainer}
          data-rtl={rtl}
          style={
            {
              '--bg-image-1024': `url('${imagesBasePath}/img-newsletter_1024x400.webp')`,
              '--bg-image-1536': `url('${imagesBasePath}/img-newsletter_1536x400.webp')`,
              '--bg-image-1920': `url('${imagesBasePath}/img-newsletter_1920x400.webp')`,
              '--bg-image-2048': `url('${imagesBasePath}/img-newsletter_2048x400.webp')`,
              '--bg-image-2304': `url('${imagesBasePath}/img-newsletter_2304x400.webp')`,
              '--bg-image-3096': `url('${imagesBasePath}/img-newsletter_3096x400.webp')`,
              '--bg-image-3840': `url('${imagesBasePath}/img-newsletter_3840x400.webp')`,
              '--bg-image-5760': `url('${imagesBasePath}/img-newsletter_5760x400.webp')`,
            } as CSSProperties
          }
        />
        <div className={styles.formContainer}>{formContent}</div>
      </div>
    )
  }

  return formContent
}

SubscriptionBox.displayName = 'SubscriptionBox'
export default withErrorBoundary(SubscriptionBox)
